import React from 'react';
import { Link } from 'react-router-dom';

/**
 * ArticleTemplate - Reusable template for blog posts, service pages, and archives
 * 
 * Usage:
 * 1. Copy this file and rename it (e.g., CUETPGGuidePage.js)
 * 2. Update the articleData object with your content
 * 3. Add the route in App.js
 */

const ArticleTemplate = () => {
  // ============================================================================
  // ARTICLE DATA - Modify this section for each article
  // ============================================================================
  const articleData = {
    // Category displayed in header
    category: 'Blog',
    categorySlug: '/blogs',
    
    // Breadcrumb path
    breadcrumbs: [
      { label: 'Home', path: '/' },
      { label: 'Blogs', path: '/blogs' },
      { label: 'CUET PG 2025 Guide', path: null } // Current page (no link)
    ],
    
    // Article metadata
    title: 'CUET PG 2025: Your Comprehensive Guide to Success',
    author: 'Dr. Rajesh Kumar',
    authorImage: '/authors/rajesh-kumar.jpg', // Optional
    publishedDate: 'January 10, 2025',
    readTime: '8 min read',
    
    // Cover image
    coverImage: '/blog-images/cuet-pg-guide.jpg',
    coverImageAlt: 'CUET PG 2025 Preparation Guide',
    
    // Tags (optional)
    tags: ['CUET PG', 'Exam Preparation', '2025', 'Study Guide'],
    
    // Related articles for sidebar
    relatedArticles: [
      {
        title: 'CUET UG 2026 Exam Pattern & Complete Syllabus',
        path: '/cuet-ug',
        category: 'CUET UG'
      },
      {
        title: 'CUET PG 2026 Counselling: Step-by-Step Guide',
        path: '/cuet-pg-counselling',
        category: 'CUET PG'
      },
      {
        title: 'UGC NET Paper 1 Mock Test — Free Practice',
        path: '/ugc-net-mock',
        category: 'UGC NET'
      },
      {
        title: 'Previous Year Question Papers (CUET UG & PG)',
        path: '/pyq-cuet',
        category: 'Resources'
      },
      {
        title: 'CUET UG Mock Test — Real Exam Simulator',
        path: '/cuet-ug-mock-test',
        category: 'Mock Tests'
      }
    ],
    
    // Quick links for sidebar (updated to real pages)
    quickLinks: [
      { label: '📥 Download Study Material', path: '/study-materials' },
      { label: '🧪 Take Free Mock Test', path: '/cuet-ug-mock-test' },
      { label: '📄 Previous Year Papers', path: '/pyq-cuet' },
      { label: '🎓 Free Counselling', path: '/cuet-counselling' },
      { label: '📰 Current Affairs Test', path: '/current-affairs-test' },
      { label: '💳 Subscription Plans', path: '/subscriptions' }
    ]
  };

  // ============================================================================
  // MAIN CONTENT - Modify this section for each article
  // ============================================================================
  const MainContent = () => (
    <div className="prose prose-invert max-w-none">
      {/* Introduction */}
      <p className="text-lg text-text-muted leading-relaxed">
        The Common University Entrance Test for Postgraduate programs (CUET PG) has become the gateway 
        to prestigious central universities across India. With the 2025 examination approaching, 
        it's crucial to have a well-structured preparation strategy. This comprehensive guide will 
        walk you through everything you need to know about CUET PG 2025.
      </p>

      {/* Section 1 */}
      <h2 className="text-2xl font-bold text-text-light mt-10 mb-4 flex items-center gap-3">
        <span className="w-8 h-8 bg-primary/20 rounded-lg flex items-center justify-center text-primary text-sm">1</span>
        What is CUET PG?
      </h2>
      <p className="text-text-muted leading-relaxed">
        CUET PG is a national-level entrance examination conducted by the National Testing Agency (NTA) 
        for admission to various postgraduate programs offered by Central Universities, State Universities, 
        Deemed Universities, and Private Universities across India.
      </p>
      <p className="text-text-muted leading-relaxed">
        The examination tests candidates on their domain knowledge, language proficiency, and general 
        awareness, making it a comprehensive assessment of their readiness for postgraduate studies.
      </p>

      {/* Info Box */}
      <div className="bg-primary/10 border border-primary/30 rounded-xl p-6 my-6">
        <h4 className="text-primary font-semibold mb-3 flex items-center gap-2">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Key Highlights of CUET PG 2025
        </h4>
        <ul className="space-y-2 text-text-muted">
          <li className="flex items-start gap-2">
            <span className="text-primary mt-1">•</span>
            Expected to be conducted in May-June 2025
          </li>
          <li className="flex items-start gap-2">
            <span className="text-primary mt-1">•</span>
            Computer-based test (CBT) mode
          </li>
          <li className="flex items-start gap-2">
            <span className="text-primary mt-1">•</span>
            Multiple language options available
          </li>
          <li className="flex items-start gap-2">
            <span className="text-primary mt-1">•</span>
            Accepted by 200+ universities nationwide
          </li>
        </ul>
      </div>

      {/* Section 2 */}
      <h2 className="text-2xl font-bold text-text-light mt-10 mb-4 flex items-center gap-3">
        <span className="w-8 h-8 bg-primary/20 rounded-lg flex items-center justify-center text-primary text-sm">2</span>
        Eligibility Criteria
      </h2>
      <p className="text-text-muted leading-relaxed">
        Before diving into preparation, ensure you meet the eligibility requirements:
      </p>
      <ul className="space-y-3 text-text-muted my-4">
        <li className="flex items-start gap-3">
          <svg className="w-5 h-5 text-green-500 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <span>Bachelor's degree from a recognized university with minimum required percentage (varies by program)</span>
        </li>
        <li className="flex items-start gap-3">
          <svg className="w-5 h-5 text-green-500 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <span>Final year students can also apply (provisional admission)</span>
        </li>
        <li className="flex items-start gap-3">
          <svg className="w-5 h-5 text-green-500 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <span>No age limit for appearing in the examination</span>
        </li>
      </ul>

      {/* Section 3 */}
      <h2 className="text-2xl font-bold text-text-light mt-10 mb-4 flex items-center gap-3">
        <span className="w-8 h-8 bg-primary/20 rounded-lg flex items-center justify-center text-primary text-sm">3</span>
        Exam Pattern & Syllabus
      </h2>
      <p className="text-text-muted leading-relaxed">
        Understanding the exam pattern is crucial for effective preparation:
      </p>
      
      {/* Table */}
      <div className="overflow-x-auto my-6">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-bg-secondary">
              <th className="border border-glass-border px-4 py-3 text-left text-text-light">Section</th>
              <th className="border border-glass-border px-4 py-3 text-left text-text-light">Questions</th>
              <th className="border border-glass-border px-4 py-3 text-left text-text-light">Marks</th>
              <th className="border border-glass-border px-4 py-3 text-left text-text-light">Duration</th>
            </tr>
          </thead>
          <tbody className="text-text-muted">
            <tr>
              <td className="border border-glass-border px-4 py-3">Domain Knowledge</td>
              <td className="border border-glass-border px-4 py-3">75</td>
              <td className="border border-glass-border px-4 py-3">300</td>
              <td className="border border-glass-border px-4 py-3">105 mins</td>
            </tr>
            <tr className="bg-bg-card/30">
              <td className="border border-glass-border px-4 py-3">Language (Optional)</td>
              <td className="border border-glass-border px-4 py-3">25</td>
              <td className="border border-glass-border px-4 py-3">100</td>
              <td className="border border-glass-border px-4 py-3">45 mins</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Section 4 */}
      <h2 className="text-2xl font-bold text-text-light mt-10 mb-4 flex items-center gap-3">
        <span className="w-8 h-8 bg-primary/20 rounded-lg flex items-center justify-center text-primary text-sm">4</span>
        Preparation Strategy
      </h2>
      <p className="text-text-muted leading-relaxed">
        A well-planned preparation strategy can make all the difference:
      </p>

      <h3 className="text-xl font-semibold text-text-light mt-6 mb-3">
        Phase 1: Foundation Building (2-3 months)
      </h3>
      <p className="text-text-muted leading-relaxed">
        Start by thoroughly understanding the syllabus and creating a study schedule. Focus on building 
        strong conceptual foundations in your chosen domain subject. Use NCERT books and standard 
        reference materials.
      </p>

      <h3 className="text-xl font-semibold text-text-light mt-6 mb-3">
        Phase 2: Deep Learning (2-3 months)
      </h3>
      <p className="text-text-muted leading-relaxed">
        Dive deeper into advanced topics. Solve previous year question papers and analyze the type of 
        questions asked. Join a reputable coaching program or online course for structured guidance.
      </p>

      <h3 className="text-xl font-semibold text-text-light mt-6 mb-3">
        Phase 3: Revision & Practice (1-2 months)
      </h3>
      <p className="text-text-muted leading-relaxed">
        Focus on revision and taking mock tests regularly. Analyze your performance and work on weak 
        areas. Practice time management to complete the exam within the given duration.
      </p>

      {/* Warning Box */}
      <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-6 my-6">
        <h4 className="text-orange-400 font-semibold mb-3 flex items-center gap-2">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          Common Mistakes to Avoid
        </h4>
        <ul className="space-y-2 text-text-muted">
          <li>• Starting preparation too late</li>
          <li>• Ignoring the negative marking scheme</li>
          <li>• Not practicing enough mock tests</li>
          <li>• Overlooking the language section</li>
        </ul>
      </div>

      {/* Section 5 */}
      <h2 className="text-2xl font-bold text-text-light mt-10 mb-4 flex items-center gap-3">
        <span className="w-8 h-8 bg-primary/20 rounded-lg flex items-center justify-center text-primary text-sm">5</span>
        Recommended Resources
      </h2>
      <p className="text-text-muted leading-relaxed">
        Here are some highly recommended resources for CUET PG preparation:
      </p>
      <ul className="space-y-3 text-text-muted my-4">
        <li className="flex items-start gap-3">
          <span className="text-accent-gold">📚</span>
          <span><strong className="text-text-light">NCERT Books:</strong> For building strong fundamentals</span>
        </li>
        <li className="flex items-start gap-3">
          <span className="text-accent-gold">📚</span>
          <span><strong className="text-text-light">Previous Year Papers:</strong> Available on NTA website</span>
        </li>
        <li className="flex items-start gap-3">
          <span className="text-accent-gold">📚</span>
          <span><strong className="text-text-light">BESST Study Materials:</strong> Comprehensive domain-specific content</span>
        </li>
        <li className="flex items-start gap-3">
          <span className="text-accent-gold">📚</span>
          <span><strong className="text-text-light">Mock Test Series:</strong> Practice with real exam-like tests</span>
        </li>
      </ul>

      {/* CTA Box */}
      <div className="bg-gradient-to-r from-primary/20 to-accent-gold/20 border border-primary/30 rounded-xl p-6 my-8">
        <h4 className="text-xl font-bold text-text-light mb-3">
          Ready to Start Your CUET PG Journey?
        </h4>
        <p className="text-text-muted mb-4">
          Join thousands of successful students who prepared with BESST. Get access to comprehensive 
          study materials, expert guidance, and unlimited mock tests.
        </p>
        <div className="flex flex-wrap gap-4">
          <Link to="/subscriptions" className="btn-primary">
            Explore Plans →
          </Link>
          <Link to="/services/mock-test-series" className="btn-secondary">
            Try Free Mock Test
          </Link>
        </div>
      </div>

      {/* Conclusion */}
      <h2 className="text-2xl font-bold text-text-light mt-10 mb-4">
        Conclusion
      </h2>
      <p className="text-text-muted leading-relaxed">
        CUET PG 2025 is your gateway to quality postgraduate education at India's top universities. 
        With proper planning, consistent effort, and the right resources, you can achieve your dream 
        of getting into your preferred university. Start your preparation today and take the first 
        step towards a bright academic future.
      </p>
      <p className="text-text-muted leading-relaxed mt-4">
        Remember, success in CUET PG is not just about hard work—it's about smart work. Follow a 
        structured approach, stay motivated, and believe in yourself. Good luck!
      </p>
    </div>
  );

  // ============================================================================
  // TEMPLATE LAYOUT - Usually no need to modify below this line
  // ============================================================================
  return (
    <div className="min-h-screen">
      {/* Category Header */}
      <div className="bg-gradient-to-r from-primary/20 via-bg-secondary to-accent-gold/20 pt-32 pb-12">
        <div className="max-w-[1400px] mx-auto px-8">
          {/* Category Badge */}
          <div className="mb-4">
            <Link 
              to={articleData.categorySlug} 
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary/20 hover:bg-primary/30 rounded-full text-primary transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
              </svg>
              {articleData.category}
            </Link>
          </div>

          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-text-muted mb-6 flex-wrap">
            {articleData.breadcrumbs.map((crumb, index) => (
              <React.Fragment key={index}>
                {index > 0 && (
                  <svg className="w-4 h-4 text-text-muted/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                )}
                {crumb.path ? (
                  <Link to={crumb.path} className="hover:text-primary transition-colors">
                    {crumb.label}
                  </Link>
                ) : (
                  <span className="text-text-light">{crumb.label}</span>
                )}
              </React.Fragment>
            ))}
          </nav>

          {/* Article Title */}
          <h1 className="text-3xl lg:text-4xl xl:text-5xl font-extrabold text-text-light mb-6 leading-tight">
            {articleData.title}
          </h1>

          {/* Article Meta */}
          <div className="flex flex-wrap items-center gap-6 text-text-muted">
            {/* Author */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center overflow-hidden">
                {articleData.authorImage ? (
                  <img src={articleData.authorImage} alt={articleData.author} className="w-full h-full object-cover" />
                ) : (
                  <span className="text-primary font-bold">{articleData.author.charAt(0)}</span>
                )}
              </div>
              <span className="font-medium text-text-light">{articleData.author}</span>
            </div>

            {/* Divider */}
            <span className="text-text-muted/30">|</span>

            {/* Published Date */}
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span>{articleData.publishedDate}</span>
            </div>

            {/* Divider */}
            <span className="text-text-muted/30">|</span>

            {/* Read Time */}
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{articleData.readTime}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="max-w-[1400px] mx-auto px-8 py-12">
        <div className="flex flex-col lg:flex-row gap-12">
          {/* Article Content */}
          <article className="flex-1 min-w-0">
            {/* Cover Image */}
            <div className="rounded-2xl overflow-hidden mb-10 bg-bg-secondary">
              {articleData.coverImage ? (
                <img 
                  src={articleData.coverImage} 
                  alt={articleData.coverImageAlt} 
                  className="w-full h-auto object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : null}
              <div className="h-64 lg:h-96 bg-gradient-to-br from-primary/20 to-accent-gold/20 flex items-center justify-center" style={{ display: articleData.coverImage ? 'none' : 'flex' }}>
                <span className="text-6xl">📖</span>
              </div>
            </div>

            {/* Tags */}
            {articleData.tags && articleData.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-8">
                {articleData.tags.map((tag, index) => (
                  <span 
                    key={index}
                    className="px-3 py-1 bg-bg-secondary text-text-muted text-sm rounded-full border border-glass-border"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            )}

            {/* Main Content */}
            <MainContent />

            {/* Share Section */}
            <div className="mt-12 pt-8 border-t border-glass-border">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <span className="text-text-light font-semibold">Share this article:</span>
                <div className="flex items-center gap-3">
                  <button className="w-10 h-10 rounded-full bg-[#1877F2]/20 hover:bg-[#1877F2]/30 text-[#1877F2] flex items-center justify-center transition-colors">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.77 7.46H14.5v-1.9c0-.9.6-1.1 1-1.1h3V.5h-4.33C10.24.5 9.5 3.44 9.5 5.32v2.15h-3v4h3v12h5v-12h3.85l.42-4z"/></svg>
                  </button>
                  <button className="w-10 h-10 rounded-full bg-[#1DA1F2]/20 hover:bg-[#1DA1F2]/30 text-[#1DA1F2] flex items-center justify-center transition-colors">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M23.44 4.83c-.8.37-1.5.38-2.22.02.93-.56.98-.96 1.32-2.02-.88.52-1.86.9-2.9 1.1-.82-.88-2-1.43-3.3-1.43-2.5 0-4.55 2.04-4.55 4.54 0 .36.03.7.1 1.04-3.77-.2-7.12-2-9.36-4.75-.4.67-.6 1.45-.6 2.3 0 1.56.8 2.95 2 3.77-.74-.03-1.44-.23-2.05-.57v.06c0 2.2 1.56 4.03 3.64 4.44-.67.2-1.37.2-2.06.08.58 1.8 2.26 3.12 4.25 3.16C5.78 18.1 3.37 18.74 1 18.46c2 1.3 4.4 2.04 6.97 2.04 8.35 0 12.92-6.92 12.92-12.93 0-.2 0-.4-.02-.6.9-.63 1.96-1.22 2.56-2.14z"/></svg>
                  </button>
                  <button className="w-10 h-10 rounded-full bg-[#0A66C2]/20 hover:bg-[#0A66C2]/30 text-[#0A66C2] flex items-center justify-center transition-colors">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                  </button>
                  <button className="w-10 h-10 rounded-full bg-[#25D366]/20 hover:bg-[#25D366]/30 text-[#25D366] flex items-center justify-center transition-colors">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                  </button>
                  <button 
                    onClick={() => navigator.clipboard.writeText(window.location.href)}
                    className="w-10 h-10 rounded-full bg-text-muted/20 hover:bg-text-muted/30 text-text-muted flex items-center justify-center transition-colors"
                    title="Copy link"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </article>

          {/* Sidebar */}
          <aside className="w-full lg:w-80 xl:w-96 flex-shrink-0">
            <div className="lg:sticky lg:top-28 space-y-8">
              {/* Related Articles */}
              <div className="glass-card">
                <h3 className="text-lg font-bold text-text-light mb-4 flex items-center gap-2">
                  <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                  </svg>
                  Related Articles
                </h3>
                <div className="space-y-4">
                  {articleData.relatedArticles.map((article, index) => (
                    <Link 
                      key={index}
                      to={article.path}
                      className="block group"
                    >
                      <span className="text-xs text-primary">{article.category}</span>
                      <h4 className="text-text-light group-hover:text-primary transition-colors line-clamp-2 mt-1">
                        {article.title}
                      </h4>
                      {index < articleData.relatedArticles.length - 1 && (
                        <div className="border-b border-glass-border mt-4" />
                      )}
                    </Link>
                  ))}
                </div>
              </div>

              {/* Quick Links */}
              {articleData.quickLinks && articleData.quickLinks.length > 0 && (
                <div className="glass-card">
                  <h3 className="text-lg font-bold text-text-light mb-4 flex items-center gap-2">
                    <svg className="w-5 h-5 text-accent-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    Quick Links
                  </h3>
                  <div className="space-y-2">
                    {articleData.quickLinks.map((link, index) => (
                      <Link 
                        key={index}
                        to={link.path}
                        className="flex items-center gap-3 p-3 rounded-xl bg-bg-secondary hover:bg-primary/10 transition-colors group"
                      >
                        <svg className="w-5 h-5 text-text-muted group-hover:text-primary transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                        <span className="text-text-light group-hover:text-primary transition-colors">{link.label}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Newsletter Subscription */}
              <div className="glass-card bg-gradient-to-br from-primary/10 to-accent-gold/10">
                <h3 className="text-lg font-bold text-text-light mb-2">Stay Updated</h3>
                <p className="text-text-muted text-sm mb-4">
                  Get the latest study tips and exam updates delivered to your inbox.
                </p>
                <form className="space-y-3">
                  <input
                    type="email"
                    placeholder="Enter your email"
                    className="w-full px-4 py-3 bg-bg-secondary border border-glass-border rounded-xl text-text-light placeholder-text-muted focus:outline-none focus:border-primary transition-colors"
                  />
                  <button type="submit" className="w-full btn-primary py-3">
                    Subscribe
                  </button>
                </form>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default ArticleTemplate;